import { createSlice } from '@reduxjs/toolkit';
import LoadingStatus from 'enums/loadingStatus';
import HousingType from 'enums/housingType';
import LotStatus from '../enums/lotStatus';
import { deriveActiveChip, deriveChipByHierarchy, countLotsByChip } from '../services/mapChipService';
import {
  TAB_FLOOR_PLANS,
  TAB_QUICK_MOVE_INS,
  VIEW_LISTING,
  VIEW_LOT_MAP_SELECTION,
  VIEW_MOBILE_LOT_CARD,
} from '../constants';

/** Redux slice key used to mount this reducer in the shared store. */
export const DATA_SLICE_KEY = 'nativeInteractiveSiteMapData';

// searchParameters fields fed by the Filters modal, excluding lotStatus — that one
// has its own default (LotStatus.Available) and its own reset derivation (deriveChipByHierarchy),
// unlike these, which simply go back to "no filter". Shared by initialState and viewReset so the
// two can't drift apart. See STORE_DESIGN.md#state-shape ("Filters Modal Field Mapping" and
// "Range Filter Semantics") for what each field means and how each Filters modal component's own
// emitted value is meant to map onto it.
const DEFAULT_FILTER_PARAMETERS = {
  price: { minimum: null, maximum: null },
  bedroom: { minimum: null, maximum: null },
  bathroom: { minimum: null, maximum: null },
  squareFeet: { minimum: null, maximum: null },
  moveInDateRange: null,
  housingType: null,
};

// Range fields are objects, so a plain spread would share them between searchParameters and
// filterDraft — copy them one level deeper so the two never alias.
function cloneSearchParameters(searchParameters) {
  return {
    ...searchParameters,
    price: { ...searchParameters.price },
    bedroom: { ...searchParameters.bedroom },
    bathroom: { ...searchParameters.bathroom },
    squareFeet: { ...searchParameters.squareFeet },
  };
}

const INITIAL_SEARCH_PARAMETERS = {
  ...DEFAULT_FILTER_PARAMETERS,
  lotStatus: LotStatus.Available.toString(),
};

export function createNativeInteractiveSiteMapDataSlice({
  fetchSiteMapStatus = LoadingStatus.Idle.toString(),
  fetchSiteMapError = null,
  fetchLotInventoryStatus = LoadingStatus.Idle.toString(),
  fetchLotInventoryError = null,
} = {}) {
  return createSlice({
    name: DATA_SLICE_KEY,
    initialState: {
      fetchSiteMapStatus,
      fetchSiteMapError,
      fetchLotInventoryStatus,
      fetchLotInventoryError,
      lots: [],
      housePlans: [],
      housingUnits: [],
      soldLots: [],
      searchParameters: cloneSearchParameters(INITIAL_SEARCH_PARAMETERS),
      filterDraft: cloneSearchParameters(INITIAL_SEARCH_PARAMETERS),
      viewState: {
        activeView: VIEW_LISTING,
        activeViewContext: {
          planId: null,
          selectedLotId: null,
          housingUnitId: null,
        },
        selectedTab: TAB_FLOOR_PLANS,
        viewHistory: [],
      },
    },
    reducers: {
      fetchSiteMap: (state) => {
        state.fetchSiteMapStatus = LoadingStatus.Loading.toString();
        state.fetchSiteMapError = null;
      },
      fetchSiteMapSuccess: (state, action) => {
        state.fetchSiteMapStatus = LoadingStatus.Succeeded.toString();
        state.lots = action.payload?.lots;
      },
      fetchSiteMapError: (state, action) => {
        state.fetchSiteMapStatus = LoadingStatus.Failed.toString();
        state.fetchSiteMapError = action.payload;
      },
      fetchLotInventory: (state) => {
        state.fetchLotInventoryStatus = LoadingStatus.Loading.toString();
        state.fetchLotInventoryError  = null;
      },
      fetchLotInventorySuccess: (state, action) => {
        state.fetchLotInventoryStatus = LoadingStatus.Succeeded.toString();

        const inventoryLots = action.payload?.lots ?? [];
        const inventoryMap  = Object.fromEntries(
          inventoryLots.map((lot) => [lot.sitePlanLotId, lot])
        );

        // Additive enrichment only site-plan fields (status, housePlanIds, housingUnitId)
        // are left untouched; inventory adds display/geometry data not present in the site-plan
        // call, plus the integer lotId — housePlans (Lots[]) and housingUnits (LotId) reference
        // lots by that integer id, not by the site-plan's sitePlanLotId string.
        // Direct Immer mutation means only matched lots get new object references, keeping
        // unmatched lots stable for distinctUntilChanged selectors.
        state.lots.forEach((lot) => {
          const inventoryLot = inventoryMap[lot.sitePlanLotId];

          if (!inventoryLot) {
            return;
          }

          lot.lotId        = inventoryLot.lotId;
          lot.lotSize      = inventoryLot.lotSize;
          lot.formattedLotSize = inventoryLot.formattedLotSize;
          lot.displayLabel = inventoryLot.displayLabel;
          lot.lotGroupName = inventoryLot.lotGroupName;
          lot.lotType      = inventoryLot.lotType;
          lot.lotFacing    = inventoryLot.lotFacing;
        });

        state.housePlans   = action.payload?.housePlans   ?? [];
        state.housingUnits = action.payload?.housingUnits ?? [];
        state.soldLots     = action.payload?.soldLots     ?? [];
      },
      fetchLotInventoryError: (state, action) => {
        state.fetchLotInventoryStatus = LoadingStatus.Failed.toString();
        state.fetchLotInventoryError  = action.payload;
      },
      lotSelected: (state, action) => {
        state.viewState.activeViewContext.selectedLotId = action.payload ?? null;
      },
      floorPlanCardSelected: (state, action) => {
        state.viewState.activeViewContext.planId = action.payload ?? null;
        state.viewState.activeViewContext.housingUnitId = null;
      },
      housingUnitCardSelected: (state, action) => {
        state.viewState.activeViewContext.planId = null;
        state.viewState.activeViewContext.housingUnitId = action.payload ?? null;
      },
      // Filters modal draft. The modal's fields only ever write into `filterDraft`;
      // nothing they do reaches `searchParameters` — and so the map, the panel or the chip — until
      // "Show XY Results" dispatches filtersApplied. Closing the modal any other way (X, Escape)
      // just leaves the draft behind; the next open re-seeds it from what's applied.
      filterDraftStarted: (state) => {
        state.filterDraft = cloneSearchParameters(state.searchParameters);
      },
      filterDraftPriceRangeChanged: (state, action) => {
        state.filterDraft.price = action.payload;
      },
      filterDraftBedroomRangeChanged: (state, action) => {
        state.filterDraft.bedroom = action.payload;
      },
      filterDraftBathroomRangeChanged: (state, action) => {
        state.filterDraft.bathroom = action.payload;
      },
      filterDraftSquareFeetRangeChanged: (state, action) => {
        state.filterDraft.squareFeet = action.payload;
      },
      filterDraftMoveInDateRangeChanged: (state, action) => {
        state.filterDraft.moveInDateRange = action.payload;
      },
      // 'Model homes' isn't a field on housePlans/housingUnits — model-home status lives on the lot
      // (lotStatus, the same thing the map chip bar drives), so picking it also moves the draft's
      // chip to Model; filtersApplied then switches the real chip.
      filterDraftHousingTypeChanged: (state, action) => {
        state.filterDraft.housingType = action.payload;
        if (action.payload === HousingType.ModelHomes.toString()) {
          state.filterDraft.lotStatus = LotStatus.Model.toString();
        }
      },
      // "Clear all filters" (Filters modal footer). Only the modal's own six fields go back to "no
      // filter", and only in the draft — like every other field edit, it's applied by "Show XY
      // Results". The draft's lotStatus goes back to the applied chip (undoing a "Model homes" pick
      // made in this same session) rather than being reset: the chip bar is a separate control.
      filterDraftCleared: (state) => {
        state.filterDraft = {
          ...cloneSearchParameters(DEFAULT_FILTER_PARAMETERS),
          lotStatus: state.searchParameters.lotStatus,
        };
      },
      // "Show XY Results". Copies the draft's six fields over searchParameters. The draft's
      // lotStatus is NOT copied here: when it differs, filtersModal.plugin dispatches chipSelected
      // right after, so every chip-switch listener (epics, tracking) still sees the one action it
      // already reacts to.
      filtersApplied: (state) => {
        const draft = cloneSearchParameters(state.filterDraft);
        const hasNewMoveInDate = draft.moveInDateRange != null &&
          draft.moveInDateRange !== state.searchParameters.moveInDateRange;
        Object.keys(DEFAULT_FILTER_PARAMETERS).forEach((key) => {
          state.searchParameters[key] = draft[key];
        });
        // Move-in date only narrows Quick Move-Ins (floor plans have no move-in date), so applying
        // a new one while the Available chip is active switches to that tab — staying on Floor
        // Plans would show no change and read as the filter not having been applied.
        const isAvailableChip = deriveActiveChip(state.searchParameters) === LotStatus.Available.toString();
        if (hasNewMoveInDate && isAvailableChip) {
          state.viewState.selectedTab = TAB_QUICK_MOVE_INS;
        }
      },
      chipSelected: (state, action) => {
        state.searchParameters.lotStatus = action.payload ?? null;
        // Tabs only exist for the Available chip (NHS20-8054) — clear selectedTab for every
        // other chip, and default it back to Floor Plans when Available becomes active again.
        state.viewState.selectedTab = deriveActiveChip(state.searchParameters) === LotStatus.Available.toString()
          ? TAB_FLOOR_PLANS
          : null;
        state.viewState.activeView = VIEW_LISTING;
        state.viewState.activeViewContext = { planId: null, selectedLotId: null, housingUnitId: null };
        state.viewState.viewHistory = [];
      },
      tabSelected: (state, action) => {
        state.viewState.selectedTab = action.payload;
      },
      // Marker action for mapBackgroundClickedEpic — that epic decides the real transition
      // (navigateBack vs. clearing the selection) based on viewHistory; this reducer is a no-op.
      mapBackgroundClicked: () => {},
      navigateTo: (state, action) => {
        // Push history only when the destination view is different from the current view,
        // and snapshot the current context/tab so navigateBack can restore it.
        // Transitions into or out of mobileLotCard are excluded from history on purpose.
        const isViewChanging = state.viewState.activeView !== action.payload.view;

        const isMobileLotCardTransition =
          state.viewState.activeView === VIEW_MOBILE_LOT_CARD ||
          action.payload.view === VIEW_MOBILE_LOT_CARD;

        if (isViewChanging && !isMobileLotCardTransition) {
          state.viewState.viewHistory.push({
            activeView: state.viewState.activeView,
            activeViewContext: { ...state.viewState.activeViewContext },
            selectedTab: state.viewState.selectedTab,
          });
        }

        // Always update activeView when changing views.
        if (isViewChanging) {
          state.viewState.activeView = action.payload.view;
        }

        state.viewState.activeViewContext = action.payload.context;
      },
      // A different lot picked for the plan already open in floorPlanDetail (desktop — see
      // selectLotRoutingOutcome). selectedLotId itself was already moved by lotSelected; this
      // rewrites every lotMapSelection snapshot in viewHistory to the new lot, so Back opens the
      // picker for the lot now on screen instead of the one selected before the change. planId is
      // cleared too, matching the context a fresh lot click into lotMapSelection produces (see
      // services/lotRoutingService.js) — the snapshot otherwise keeps the plan picked from it,
      // which would narrow the map to that plan's lots rather than to the picker's own lot.
      detailLotChanged: (state, action) => {
        state.viewState.viewHistory.forEach((entry) => {
          if (entry.activeView === VIEW_LOT_MAP_SELECTION) {
            entry.activeViewContext = {
              ...entry.activeViewContext,
              planId: null,
              selectedLotId: action.payload ?? null,
            };
          }
        });
      },
      navigateBack: (state) => {
        const previous = state.viewState.viewHistory.pop();
        if (!previous) {
          return;
        }
        state.viewState.activeView = previous.activeView;
        // Listing's own activeViewContext is always fully null (see chipSelected/viewReset) —
        // but the entry pushed for it in navigateTo can carry a stale, non-null selectedLotId:
        // navigateTo's history push snapshots activeViewContext AFTER lotSelected(lotId) has
        // already mutated it (both dispatched from the same lot click, see lotSelectedEpic.js),
        // so a lot click that routes straight out of listing records the just-clicked lot as if
        // it were already selected on listing itself. Left uncorrected, navigating back (or a
        // background click, which also calls navigateBack once view history exists) restores
        // that stale selection and the lot never visually deselects. Force the same clean-slate
        // context chipSelected/viewReset already use rather than trusting the popped snapshot.
        state.viewState.activeViewContext = previous.activeView === VIEW_LISTING
          ? { planId: null, selectedLotId: null, housingUnitId: null }
          : previous.activeViewContext;
        state.viewState.selectedTab = previous.selectedTab;
      },
      // Dispatched on mobile-modal teardown (see index.js's onStop) — the dataSlice itself is
      // created once per page and outlives any single modal open/close cycle, so without this,
      // reopening the modal after having navigated into a sub-view (e.g. lotMapSelection) would
      // resume there instead of starting fresh at the default listing view.
      viewReset: (state) => {
        state.viewState.activeView = VIEW_LISTING;
        state.viewState.activeViewContext = { planId: null, selectedLotId: null, housingUnitId: null };
        // searchParameters.lotStatus is reset too (not left as-is) — a stale chip (e.g. Sold)
        // surviving a reopen reads as the same "didn't actually reset" bug to the user as a
        // stale view would. Derived via the same Map Chips Display Logic hierarchy
        // (deriveChipByHierarchy, NHS20-8054) mapChips.plugin already uses when the active chip
        // empties out, rather than hard-coding Available — a community with 0 Available lots
        // would otherwise reset onto a chip with nothing to show. Falls back to the current
        // value only if every chip is empty (e.g. lots haven't loaded yet).
        const hierarchyChip = deriveChipByHierarchy(countLotsByChip(state.lots));
        // Every other filter (Price, Bedrooms, Bathrooms, Size, Move-in date, Housing type) resets
        // to "no filter" here too, for the same reason: left as-is, a Filters modal filter that
        // survived a reopen would be the same stale-state bug as a stale chip or a stale view.
        state.searchParameters = {
          ...DEFAULT_FILTER_PARAMETERS,
          lotStatus: hierarchyChip?.key ?? state.searchParameters.lotStatus,
        };
        state.viewState.selectedTab = deriveActiveChip(state.searchParameters) === LotStatus.Available.toString()
          ? TAB_FLOOR_PLANS
          : null;
        state.viewState.viewHistory = [];
      },
    },
  });
}
